/**
 * ============================================================
 * SMART COLOR SEARCH — SHOPIFY
 * ============================================================
 *
 * Uses Shopify Search & Discovery metafield filter:
 * filter.p.m.custom.color
 *
 * Examples:
 *
 * blue
 * -> All products with Color = Blue
 *
 * blue dress
 * -> All products with Color = Blue
 *
 * blue dresses
 * -> All products with Color = Blue
 *
 * red gown
 * -> All products with Color = Red
 *
 * blue midi
 * -> Color = Blue + search for "midi"
 *
 * red sequin dress
 * -> Color = Red + search for "sequin dress"
 *
 * Amy
 * -> Normal Shopify search
 *
 * ============================================================
 */

(() => {
  'use strict';


  /* =========================================================
     SETTINGS
  ========================================================= */

  const FILTER_KEY = 'filter.p.m.custom.color';

  const STORAGE_KEY = 'smartColorSearchDisplay';


  /*
   * Search word -> EXACT Color metafield value in Shopify.
   *
   * Left side:
   * what customer may type.
   *
   * Right side:
   * value used by Shopify Color filter.
   */
  const COLORS = {
    beige: 'Beige',
    black: 'Black',
    blue: 'Blue',
    brown: 'Brown',
    gold: 'Gold',

    gray: 'Gray',
    grey: 'Gray',

    green: 'Green',
    orange: 'Orange',
    pink: 'Pink',
    purple: 'Purple',
    red: 'Red',
    silver: 'Silver',
    turquoise: 'Turquoise',
    white: 'White',
    yellow: 'Yellow'
  };


  /*
   * These words are GENERIC.
   *
   * If customer searches:
   *
   * blue dress
   * blue dresses
   * blue gown
   *
   * we DON'T use these words as text search.
   *
   * We only use:
   *
   * Color = Blue
   *
   * This guarantees the result count matches
   * Shopify's actual Color filter.
   */
  const GENERIC_PRODUCT_WORDS = [
    'dress',
    'dresses',

    'gown',
    'gowns',

    'clothing',
    'clothes',

    'outfit',
    'outfits',

    'product',
    'products',

    'item',
    'items'
  ];


  /* =========================================================
     BASIC HELPERS
  ========================================================= */

  function clean(text) {
    return String(text || '')
      .replace(/\s+/g, ' ')
      .trim();
  }


  function escapeRegExp(text) {
    return String(text)
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }


  function getSearchPath() {
    /*
     * Supports normal Shopify URLs
     * and stores using language subfolders.
     */

    try {
      const root =
        window.Shopify &&
        window.Shopify.routes &&
        typeof window.Shopify.routes.root === 'string'
          ? window.Shopify.routes.root
          : '/';

      return (
        root.replace(/\/?$/, '/') + 'search'
      ).replace(/\/{2,}/g, '/');

    } catch (error) {
      return '/search';
    }
  }


  function isSearchPage() {
    return window.location.pathname
      .replace(/\/+$/, '')
      .endsWith('/search');
  }


  /* =========================================================
     PARSE CUSTOMER SEARCH
  ========================================================= */

  function parseQuery(originalQuery) {

    let remaining = clean(originalQuery);

    const foundColors = [];


    /*
     * Check longest color names first.
     * Useful if multi-word aliases are added later.
     */
    const aliases = Object.keys(COLORS)
      .sort((a, b) => b.length - a.length);


    aliases.forEach((alias) => {

      const regex = new RegExp(
        '\\b' +
        escapeRegExp(alias) +
        '\\b',
        'gi'
      );


      if (!regex.test(remaining)) {
        return;
      }


      const colorValue = COLORS[alias];


      if (!foundColors.includes(colorValue)) {
        foundColors.push(colorValue);
      }


      /*
       * Remove color from text query.
       *
       * "blue dress"
       * becomes:
       * "dress"
       */
      remaining = remaining.replace(
        regex,
        ' '
      );

    });


    /*
     * No color found.
     *
     * Shopify search should work completely normally.
     */
    if (!foundColors.length) {
      return {
        colors: [],
        text: clean(originalQuery)
      };
    }


    /*
     * Clean connector words.
     *
     * red and black dress
     * ->
     * dress
     */
    remaining = remaining
      .replace(/\b(and|or)\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();


    /*
     * =====================================================
     * IMPORTANT ACCURACY FIX
     * =====================================================
     *
     * If everything left after removing the color
     * is only generic product wording:
     *
     * blue dress
     * blue dresses
     * blue gown
     *
     * remove the text search completely.
     *
     * Result:
     *
     * q=*
     * Color=Blue
     *
     * Therefore it matches Shopify's Color filter count.
     */
    if (remaining) {

      const words = remaining
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);


      const onlyGenericWords =
        words.length > 0 &&
        words.every((word) =>
          GENERIC_PRODUCT_WORDS.includes(word)
        );


      if (onlyGenericWords) {
        remaining = '';
      }

    }


    return {
      colors: foundColors,
      text: remaining
    };
  }


  /* =========================================================
     CREATE SHOPIFY SEARCH URL
  ========================================================= */

  function buildSearchUrl(originalQuery) {

    const parsed =
      parseQuery(originalQuery);


    /*
     * No color?
     * Do NOT interfere with Shopify.
     */
    if (!parsed.colors.length) {
      return null;
    }


    const url = new URL(
      getSearchPath(),
      window.location.origin
    );


    /*
     * IMPORTANT:
     *
     * If there is no meaningful text after
     * removing the color, use "*".
     *
     * Example:
     *
     * blue
     * blue dress
     *
     * both become:
     *
     * q=*
     * filter Color=Blue
     */
    url.searchParams.set(
      'q',
      parsed.text || '*'
    );


    /*
     * Only products.
     */
    url.searchParams.set(
      'type',
      'product'
    );


    /*
     * Add exact Shopify metafield filter.
     */
    parsed.colors.forEach((color) => {

      url.searchParams.append(
        FILTER_KEY,
        color
      );

    });


    url.searchParams.set(
      'sort_by',
      'relevance'
    );


    return url;
  }


  /* =========================================================
     REDIRECT
  ========================================================= */

  function goToSmartSearch(
    originalQuery,
    replaceHistory = false
  ) {

    const url =
      buildSearchUrl(originalQuery);


    if (!url) {
      return false;
    }


    /*
     * Save exactly what customer typed.
     *
     * This allows us to display:
     *
     * blue dress
     *
     * while q=* remains hidden in background.
     */
    try {

      sessionStorage.setItem(
        STORAGE_KEY,
        clean(originalQuery)
      );

    } catch (error) {
      /*
       * Search still works even if
       * sessionStorage is unavailable.
       */
    }


    if (replaceHistory) {

      window.location.replace(
        url.toString()
      );

    } else {

      window.location.assign(
        url.toString()
      );

    }


    return true;
  }


  /* =========================================================
     STANDARD SHOPIFY SEARCH FORM
  ========================================================= */

  document.addEventListener(
    'submit',

    function (event) {

      const form =
        event.target;


      if (
        !(form instanceof HTMLFormElement)
      ) {
        return;
      }


      const input =
        form.querySelector(
          'input[name="q"]'
        );


      if (!input) {
        return;
      }


      const query =
        clean(input.value);


      if (!query) {
        return;
      }


      const parsed =
        parseQuery(query);


      /*
       * Normal search:
       *
       * Amy
       * evening
       * collection name
       *
       * Shopify handles it normally.
       */
      if (!parsed.colors.length) {
        return;
      }


      /*
       * Color detected.
       *
       * Stop normal Shopify text search
       * and use our accurate Color filter.
       */
      event.preventDefault();

      event.stopImmediatePropagation();


      goToSmartSearch(query);

    },

    true
  );


  /* =========================================================
     ENTER KEY SUPPORT
  ========================================================= */

  /*
   * Some Shopify themes/search apps intercept
   * Enter before the normal form submission.
   *
   * This makes the color search still work.
   */
  document.addEventListener(
    'keydown',

    function (event) {

      if (
        event.key !== 'Enter' ||
        event.isComposing
      ) {
        return;
      }


      const input =
        event.target;


      if (
        !(input instanceof HTMLInputElement) ||
        input.name !== 'q'
      ) {
        return;
      }


      const query =
        clean(input.value);


      if (!query) {
        return;
      }


      const parsed =
        parseQuery(query);


      if (!parsed.colors.length) {
        return;
      }


      event.preventDefault();

      event.stopImmediatePropagation();


      goToSmartSearch(query);

    },

    true
  );


  /* =========================================================
     FAILSAFE
  ========================================================= */

  /*
   * Suppose another Shopify search script
   * opens:
   *
   * /search?q=blue+dress
   *
   * before our submit listener catches it.
   *
   * When the page loads, this detects it
   * and automatically repairs it.
   */
  function repairSearchPage() {

    if (!isSearchPage()) {
      return;
    }


    const params =
      new URLSearchParams(
        window.location.search
      );


    /*
     * Already contains our Color filter.
     *
     * Do nothing.
     *
     * This prevents redirect loops.
     */
    if (
      params.has(FILTER_KEY)
    ) {
      return;
    }


    const query =
      clean(
        params.get('q')
      );


    if (
      !query ||
      query === '*'
    ) {
      return;
    }


    const parsed =
      parseQuery(query);


    if (!parsed.colors.length) {
      return;
    }


    goToSmartSearch(
      query,
      true
    );
  }


  /* =========================================================
     FRIENDLY SEARCH BOX
  ========================================================= */

  /*
   * Shopify needs:
   *
   * q=*
   *
   * for exact color-only results.
   *
   * But customer should NOT see "*".
   *
   * So internally:
   *
   * q=*
   *
   * while visible box shows:
   *
   * blue
   * or
   * blue dress
   */
  function updateVisibleSearchBox() {

    if (!isSearchPage()) {
      return;
    }


    const params =
      new URLSearchParams(
        window.location.search
      );


    const colors =
      params.getAll(
        FILTER_KEY
      );


    if (!colors.length) {
      return;
    }


    const q =
      clean(
        params.get('q')
      );


    let displayText = '';


    /*
     * Best option:
     * show exactly what customer typed.
     */
    try {

      displayText =
        clean(
          sessionStorage.getItem(
            STORAGE_KEY
          )
        );

    } catch (error) {
      displayText = '';
    }


    /*
     * Fallback if browser storage
     * isn't available.
     */
    if (!displayText) {

      const colorText =
        colors
          .join(' ')
          .toLowerCase();


      if (
        q &&
        q !== '*'
      ) {

        displayText =
          clean(
            colorText +
            ' ' +
            q
          );

      } else {

        displayText =
          colorText;

      }
    }


    /*
     * Update every Shopify search field
     * currently rendered on page.
     */
    document
      .querySelectorAll(
        'input[name="q"]'
      )
      .forEach((input) => {

        /*
         * Don't overwrite while customer
         * is actively typing a new search.
         */
        if (
          document.activeElement === input &&
          input.value &&
          input.value !== '*'
        ) {
          return;
        }


        input.value =
          displayText;


        input.setAttribute(
          'value',
          displayText
        );

      });

  }


  /* =========================================================
     START
  ========================================================= */

  repairSearchPage();


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',

      updateVisibleSearchBox,

      {
        once: true
      }
    );

  } else {

    updateVisibleSearchBox();

  }


  /*
   * Some themes/apps render their search
   * box a little later.
   */
  setTimeout(
    updateVisibleSearchBox,
    300
  );


  setTimeout(
    updateVisibleSearchBox,
    1000
  );

})();